/** Formatting helpers for outcome text: HL "YYYYMMDD-HHMM" UTC times, thresholds with units, countdowns. */
import { getLocale, intlLocale, t } from '../i18n/i18n';

export interface TimeFormatOptions {
  /** IANA zone; undefined = the viewer's local zone. Tests pass "UTC". */
  timeZone?: string;
  /** Reference "now" (the year is shown only when it differs from now's year). */
  now?: Date;
}

/** "20261017-0145" (UTC) → Date; undefined for anything else (including impossible dates). */
export function parseHlTime(s: string | null | undefined): Date | undefined {
  if (typeof s !== 'string') return undefined;
  const m = /^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})$/.exec(s.trim());
  if (!m) return undefined;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return undefined;
  const t = new Date(Date.UTC(y, mo - 1, d, h, mi));
  return t.getUTCDate() === d ? t : undefined; // rejects Feb 30 etc.
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function dtf(timeZone: string | undefined, withYear: boolean): Intl.DateTimeFormat {
  const loc = getLocale();
  const key = `${loc}|${timeZone ?? ''}|${withYear}`;
  let f = fmtCache.get(key);
  if (!f) {
    // #799/#869: the active locale (en keeps en-US) with its own calendar (Thai: Buddhist era, owner decision) and
    // Western digits.
    f = new Intl.DateTimeFormat(intlLocale(loc), {
      numberingSystem: 'latn',
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'short',
      ...(timeZone ? { timeZone } : {}),
    });
    fmtCache.set(key, f);
  }
  return f;
}

function yearIn(d: Date, timeZone: string | undefined): number {
  if (!timeZone) return d.getFullYear();
  if (timeZone === 'UTC') return d.getUTCFullYear();
  const y = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone }).format(d);
  return Number(y);
}

/** Date → "Oct 17, 01:45 UTC" in the viewer's zone (or opts.timeZone); the year is added when not this year. */
export function formatTime(d: Date, opts: TimeFormatOptions = {}): string {
  const now = opts.now ?? new Date();
  const withYear = yearIn(d, opts.timeZone) !== yearIn(now, opts.timeZone);
  return dtf(opts.timeZone, withYear).format(d);
}

/** Date → "2026-10-17 01:45 UTC" (the hover text next to local times). */
export function formatUtc(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

/** HL time string → local display; unparseable input is returned unchanged. */
export function formatHlTime(s: string | null | undefined, opts: TimeFormatOptions = {}): string {
  if (!s) return '';
  const d = parseHlTime(s);
  return d ? formatTime(d, opts) : s;
}

/** Time left until `d`: "in 2d 4h", "in 3h 5m", "in 12m", "in <1m"; "ended" once past (words from the catalog). */
export function formatCountdown(d: Date, now: Date = new Date()): string {
  const ms = d.getTime() - now.getTime();
  if (ms <= 0) return t('describe.countdown.ended');
  const min = Math.floor(ms / 60_000);
  const inT = (time: string) => t('describe.countdown.in', { time });
  if (min < 1) return inT(t('describe.countdown.lt_1m'));
  const days = Math.floor(min / 1440);
  const hours = Math.floor((min % 1440) / 60);
  const mins = min % 60;
  const n = String;
  if (days >= 30) return inT(t('describe.countdown.d', { d: n(days) }));
  if (days > 0) return inT(hours ? t('describe.countdown.d_h', { d: n(days), h: n(hours) }) : t('describe.countdown.d', { d: n(days) }));
  if (hours > 0) return inT(mins ? t('describe.countdown.h_m', { h: n(hours), m: n(mins) }) : t('describe.countdown.h', { h: n(hours) }));
  return inT(t('describe.countdown.m', { m: n(mins) }));
}

/**
 * Decimal string → grouped, keeping the given precision: "81300" → "81,300", "2446.4" → "2,446.4", "-1.50" → "-1.50".
 * Non-numeric input is returned trimmed and unchanged.
 */
export function formatNumber(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  const m = /^([+-]?)(\d+)(\.\d+)?$/.exec(s);
  if (!m) return s;
  const int = (m[2] ?? '').replace(/^0+(?=\d)/, '');
  return `${m[1]}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${m[3] ?? ''}`;
}

export type Unit = 'usd' | 'pct' | 'bps' | 'none';

/** Decimal places kept for values >= 1 in each unit; values below 1 keep SMALL_SIG significant digits instead. */
const UNIT_DECIMALS: Record<Unit, number> = { usd: 2, pct: 2, bps: 2, none: 2 };
const SMALL_SIG = 4;

/**
 * Round a plain decimal string to `dp` fractional digits (half away from zero) without going through a float, then
 * drop trailing fractional zeros: ("81457.954999", 2) → "81457.95", ("0.30000000000000004", 2) → "0.3".
 * Non-decimal input (including exponent notation) is returned trimmed and unchanged.
 */
export function roundDecimal(v: string, dp: number): string {
  const s = v.trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[2] && !m[3])) return s;
  const sign = m[1] === '-' ? '-' : '';
  const intPart = m[2] || '0';
  const frac = m[3] ?? '';
  if (frac.length <= dp) {
    const f = frac.replace(/0+$/, '');
    const i = intPart.replace(/^0+(?=\d)/, '');
    return (/^[0.]*$/.test(i + f) ? '' : sign) + i + (f ? `.${f}` : '');
  }
  const digits = BigInt(intPart + frac.slice(0, dp));
  const up = (frac.charCodeAt(dp) - 48) >= 5;
  const r = (digits + (up ? 1n : 0n)).toString().padStart(dp + 1, '0');
  const i = r.slice(0, r.length - dp) || '0';
  const f = r.slice(r.length - dp).replace(/0+$/, '');
  const zero = /^0*$/.test(i + f);
  return (zero ? '' : sign) + i + (f ? `.${f}` : '');
}

/**
 * Decimal string → display precision for `unit`: values with |v| >= 1 keep at most 2 decimals; smaller values keep
 * 4 significant digits so small prices (0.0005, 0.08497) are not flattened to 0. This also removes float noise
 * such as "…954999" or "…0000001" runs.
 */
export function cleanDecimal(v: string | number | null | undefined, unit: Unit = 'none'): string {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  const m = /^[+-]?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[1] && !m[2])) return s;
  const intPart = (m[1] ?? '').replace(/^0+/, '');
  if (intPart !== '') return roundDecimal(s, UNIT_DECIMALS[unit]);
  const frac = m[2] ?? '';
  const lead = frac.search(/[1-9]/);
  if (lead < 0) return roundDecimal(s, 0);
  return roundDecimal(s, Math.max(UNIT_DECIMALS[unit], lead + SMALL_SIG));
}

/** Threshold with its unit, at display precision (cleanDecimal): usd "$81,300", pct "4.25%", bps "25 bps", none "42.5". */
export function formatWithUnit(v: string | number | null | undefined, unit: Unit): string {
  const n = formatNumber(cleanDecimal(v, unit));
  if (n === '' || !/\d/.test(n)) return n;
  switch (unit) {
    case 'usd':
      return n.startsWith('-') ? `-$${n.slice(1)}` : `$${n.replace(/^\+/, '')}`;
    case 'pct':
      return `${n}%`;
    case 'bps':
      return `${n} bps`;
    default:
      return n;
  }
}

/** "xyz:XYZ100" → "XYZ100" (builder-dex prefix dropped for titles; details keep the full name). */
export function stripDex(perp: string | null | undefined): string {
  if (!perp) return '';
  const i = perp.lastIndexOf(':');
  return i >= 0 ? perp.slice(i + 1) : perp;
}

/**
 * Unit of a price threshold: yields, rates and anything whose price description says percent/APY read in %,
 * everything else in USD.
 */
export function priceUnit(underlying: string | null | undefined, priceDescription?: string | null): Unit {
  const u = stripDex(underlying ?? '').toUpperCase();
  if (/^US\d+[YM]$|YIELD|RATE$/.test(u)) return 'pct';
  if (priceDescription && /\bpercent\b|\bAP[YR]\b|%/i.test(priceDescription)) return 'pct';
  return 'usd';
}

/** "resolutionDeadline" → "Resolution deadline", "shortNameA" → "Short name A". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1).replace(/\b([a-z])$/, (c) => c.toUpperCase());
}

/** Longest title in one-line contexts (order result line, toasts); dialogs show the full title. */
export const TITLE_MAX = 60;

/** Cut at a word boundary when one is reasonably close, else hard-cut; appends an ellipsis. */
export function truncateTitle(t: string, max = TITLE_MAX): string {
  const s = t.trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const sp = cut.lastIndexOf(' ');
  return `${(sp >= max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,:;·–-]+$/, '')}…`;
}
